import React, { useRef, useCallback } from "react";

import { MessageDescriptor } from "react-intl";

import { Inline } from "@atlaskit/primitives-next";

import { TAB_KEYS } from "../constants";
import { useResourcesPageUIAnalytics } from "../hooks";

import { TabPill } from "./tab-pill";

interface TabSelectorProps {
    currentTab: TAB_KEYS;
    tabs: {
        id: TAB_KEYS;
        icon: React.ReactNode;
        text: MessageDescriptor;
    }[];
    onTabSelect: (id: TAB_KEYS) => void;
}

export const TabSelector: React.FC<TabSelectorProps> = ({
    currentTab,
    tabs,
    onTabSelect
}) => {
    const { sendResourceFilterClickUIEvent } = useResourcesPageUIAnalytics();
    const tabRefs = useRef<Map<TAB_KEYS, HTMLButtonElement>>(new Map());

    const handleTabSelect = useCallback(
        (tabId: TAB_KEYS) => {
            onTabSelect(tabId);
            sendResourceFilterClickUIEvent(tabId);
        },
        [onTabSelect, sendResourceFilterClickUIEvent]
    );

    const setTabRef = useCallback(
        (tabId: TAB_KEYS) => (el: HTMLButtonElement | null) => {
            if (el) {
                tabRefs.current.set(tabId, el);
            } else {
                tabRefs.current.delete(tabId);
            }
        },
        []
    );

    const handleKeyDown = useCallback(
        (
            event: React.KeyboardEvent<HTMLButtonElement>,
            currentIndex: number
        ) => {
            const tabCount = tabs.length;
            let nextIndex: number | null = null;

            switch (event.key) {
                case "ArrowRight":
                    nextIndex = (currentIndex + 1) % tabCount;
                    break;
                case "ArrowLeft":
                    nextIndex = (currentIndex - 1 + tabCount) % tabCount;
                    break;
                case "Home":
                    nextIndex = 0;
                    break;
                case "End":
                    nextIndex = tabCount - 1;
                    break;
                default:
                    return;
            }

            event.preventDefault();
            const nextTab = tabs[nextIndex];
            if (!nextTab) return;
            const nextTabEl = tabRefs.current.get(nextTab.id);
            nextTabEl?.focus();
            handleTabSelect(nextTab.id);
        },
        [tabs, handleTabSelect]
    );

    return (
        <Inline
            alignBlock="center"
            space="space.100"
            shouldWrap
            role="tablist"
            aria-label="Resource tabs"
            testId="tab-selector"
        >
            {tabs.map((tab, index) => (
                <TabPill
                    key={tab.id}
                    ref={setTabRef(tab.id)}
                    id={`resource-tab-${tab.id}`}
                    tabPanelId={`resource-tabpanel-${tab.id}`}
                    icon={tab.icon}
                    text={tab.text}
                    isSelected={tab.id === currentTab}
                    onSelect={() => handleTabSelect(tab.id)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                />
            ))}
        </Inline>
    );
};
